import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSelection } from '@/contexts/SelectionContext';
import { WorkbenchReviewer } from '@/components/workbench/WorkbenchReviewer';
import { WorkbenchEmptyState } from '@/components/workbench/WorkbenchEmptyState';
import { WorkbenchUploadAction } from '@/components/workbench/WorkbenchUploadAction';
import { WorkbenchReleaseMenu } from '@/components/workbench/WorkbenchReleaseMenu';
import type { BulkReleaseHandle } from '@/hooks/workbench/bulkRelease';
import { PageHeader } from '@/components/PageHeader';
import { useEntwurf } from '@/entwurf/EntwurfOptionen';
import { useProductStats } from '@/hooks/useProductStats';
import type { OptimizationSuccessContext } from '@/components/optimization/OptimizationProgressModal';

const Workbench: React.FC = () => {
  const { t } = useTranslation('workbench');
  const { selectedFeedId } = useSelection();

  /* Ausserhalb der Entwurfsseite liefert der Haken 'klassisch'/'sichtbar',
     also genau das heutige Verhalten. */
  const { kopfzone, seitentitel } = useEntwurf();
  const eineZeile = kopfzone === 'eineZeile';

  /**
   * C-19: kommt der Aufruf direkt aus dem Erfolgsbild des Fortschritts-Modals
   * (FeedHealthDashboard reicht den Kontext ueber `navigate(..., { state })`
   * durch), zeigt die Subline den gemessenen Score-Sprung statt der
   * statischen Standardzeile.
   *
   * Bewusst Router-State und keine URL/kein globaler Store: `location.state`
   * ueberlebt nur diese eine Navigation. Ein Reload dieser Seite (oder ein
   * spaeterer Besuch ueber die Sidebar) verliert ihn wieder und faellt auf
   * die statische Subline zurueck — genau richtig fuer eine Momentum-Zeile,
   * die den Schwung EINES Erfolgsmoments traegt und nicht dauerhaft
   * behauptet "du bist gerade fertig geworden".
   *
   * Review-Fix Runde 1 (Feed-Scope-Leak): `location.state` ueberlebt eine
   * REINE Feed-Auswahl-Aenderung ueber den globalen `FeedSwitcher`
   * (Layout) unveraendert, denn der wechselt den Feed ohne zu navigieren.
   * Ohne die Pruefung unten wuerde die Subline nach so einem Wechsel den
   * Score-Sprung von Feed A weiter zeigen, waehrend die Seite laengst Feed B
   * anzeigt. `successCtx.feedId` traegt genau die Feed-ID, die zum Zeitpunkt
   * der Navigation aktiv war (siehe FeedHealthDashboard.tsx) — nur wenn sie
   * mit dem AKTUELL ausgewaehlten Feed uebereinstimmt, ist die Momentum-Zeile
   * noch richtig zugeordnet.
   */
  const location = useLocation();
  const successCtxRaw = (location.state as { optimizationSuccess?: OptimizationSuccessContext } | null)
    ?.optimizationSuccess ?? null;
  const successCtx = successCtxRaw && successCtxRaw.feedId === selectedFeedId ? successCtxRaw : null;
  const subtitle = successCtx?.post != null
    ? (successCtx.pre != null
        ? t('subtitleAfterRun', { pre: Math.round(successCtx.pre), post: Math.round(successCtx.post) })
        : t('subtitleAfterRunNoPre', { post: Math.round(successCtx.post) }))
    : t('subtitle');
  // Dieselbe Abfrage wie im Upload-Knopf und in der Fortschrittsleiste
  // (Query-Key ['simple-product-stats', feedId]) — React Query liefert einen
  // Eintrag, keine zusaetzliche Netzlast. Eine zweite Quelle waere genau der
  // Fehler, den das Dashboard heute macht.
  const { stats, statsFeedId } = useProductStats(selectedFeedId, false, null, null);
  /**
   * Dieselbe Wache wie in `WorkbenchUploadAction` (`statsMatchFeed`), und aus
   * demselben Grund: `useProductStats` haelt beim Feedwechsel per
   * `placeholderData` die Zahlen des VORHERIGEN Feeds und meldet dabei nicht
   * mehr "laedt". Ohne den Vergleich boete die Uebergabe im Leerzustand einen
   * Upload fuer eine Menge an, die zu einem anderen Feed gehoert -- und
   * zurueckholen laesst sich ein Upload nicht. 0 heisst hier "noch nichts
   * gemessen"; die Uebergabe erscheint dann erst, wenn die Zahl zum Feed passt.
   */
  const approvedAwaitingUpload = statsFeedId === selectedFeedId ? stats.approvedAwaitingUpload : 0;

  /**
   * Bruecke zwischen Upload-Knopf (Kopfzeile) und Bearbeitungszustand (Karte).
   *
   * Der Knopf sitzt hier oben, damit er in jedem Leerzustand erreichbar bleibt
   * — besonders in "alles geprueft", dem Moment, in dem er gebraucht wird. Der
   * Preis dafuer war, dass er die offene Handkorrektur der Karte nicht sah und
   * den alten Text hochlud. Eine Ref statt eines States: die Anmeldung darf
   * kein Rendern ausloesen, und gelesen wird sie nur im Klick.
   */
  const flushRef = useRef<(() => Promise<boolean>) | null>(null);
  const registerFlushPendingEdits = useCallback((fn: (() => Promise<boolean>) | null) => {
    flushRef.current = fn;
  }, []);
  const flushPendingEdits = useCallback(
    () => flushRef.current?.() ?? Promise.resolve(true),
    [],
  );

  /**
   * Zweite Bruecke, diesmal fuer die Sammelfreigabe (Spec 9.1).
   *
   * Hier bewusst State und nicht Ref wie oben: der Knopf ZEIGT die Zahlen
   * ("4.812 sichere Produkte freigeben") und muss neu rendern, sobald sie sich
   * aendern. Eine Ref sammelte den neuen Stand ein, ohne dass ihn jemand sieht.
   * Der Registrar selbst haengt an `[]` und ist damit stabil - waere er es
   * nicht, meldete der Reviewer sich bei jedem Render neu an und die Anmeldung
   * liefe im Kreis.
   */
  /**
   * Der Oeffner des Upload-Dialogs, von `WorkbenchUploadAction` gemeldet und an
   * die Leerzustaende weitergereicht (Spec 2026-09-16, Abschnitt 7).
   *
   * Als Ref und NICHT als State: ein State loeste bei jedem An- und Abmelden
   * ein zusaetzliches Rendern der ganzen Seite aus, und der Wert wird nur im
   * Klick gelesen, nie gerendert.
   */
  const openUploadRef = useRef<(() => void) | null>(null);
  const registerOpenUpload = useCallback((fn: (() => void) | null) => {
    openUploadRef.current = fn;
  }, []);
  const openUpload = useCallback(() => {
    openUploadRef.current?.();
  }, []);

  const [bulkRelease, setBulkRelease] = useState<BulkReleaseHandle | null>(null);
  const registerBulkRelease = useCallback((handle: BulkReleaseHandle | null) => {
    setBulkRelease(handle);
  }, []);

  return (
    /* Die Freihaltung am unteren Rand ist entfallen: die Entscheidungsleiste
       sitzt seit Etappe 3 am Fuss der Karte und nicht mehr am Fensterrand.
       Die Seite hielt dafuer zuletzt bis zu 165 px frei - Platz, der genau der
       Flaeche fehlte, auf der entschieden wird. */
    <div className="flex flex-col h-full min-h-0 px-4 sm:px-6 lg:px-8 pt-4 pb-4">
      {/* Spec 9.1: links neben dem Upload-Knopf. Die Reihenfolge ist die des
          Arbeitsschritts - erst freigeben, dann hochladen. Der Upload-Knopf
          rendert nichts, solange nichts freigegeben ist; vor der ersten
          Freigabe steht die Sammelfreigabe also allein, und das ist genau
          richtig. */}
      {!eineZeile && (
      <div className="flex-shrink-0 mb-4">
        {/* Der Upload sitzt auf Seitenebene, damit er in beiden Ansichten und
            in JEDEM Leerzustand erreichbar bleibt — besonders in "alles
            geprueft", dem Moment, in dem er gebraucht wird. Begruendung in
            WorkbenchUploadAction.tsx. */}
        <PageHeader
          title={t('title')}
          subtitle={subtitle}
          actions={
            selectedFeedId ? (
              <>
                {bulkRelease && <WorkbenchReleaseMenu {...bulkRelease} />}
                <WorkbenchUploadAction feedId={selectedFeedId} flushPendingEdits={flushPendingEdits} registerOpenUpload={registerOpenUpload} />
              </>
            ) : undefined
          }
        />
      </div>
      )}

      {/* In der einzeiligen Kopfzone gibt es keinen PageHeader mehr. Der Titel
          bleibt trotzdem im Baum: unter 768 px ist er die EINZIGE Ueberschrift
          der Seite, weil der Brotkrumen-Container in Layout.tsx dort
          `hidden md:block` traegt. Die Schwelle ist deshalb zeichengleich
          dieselbe - der Titel verschwindet genau dort, wo die Brotkrume ihn
          wortgleich traegt. */}
      {eineZeile && (
        <div className="mb-3 flex-shrink-0">
          <h1
            className={
              seitentitel === 'nurSchmal'
                ? 'md:sr-only text-2xl font-bold tracking-tight'
                : 'text-2xl font-bold tracking-tight'
            }
          >
            {t('title')}
          </h1>
          {/* Befoerderungs-Ruling 24.08.: die STATISCHE Erklaer-Subline
              entfaellt in der einzeiligen Kopfzone bewusst (genau das spart
              die Hoehe). Die DYNAMISCHE Erfolgs-Zeile (C-19, nur nach einem
              Optimierungslauf, feed-gescoped) ist davon getrennt - sie ohne
              Platz zu lassen waere ein stiller Feature-Verlust, kein
              Platzgewinn: sie steht ohnehin nur den einen Moment da. */}
          {successCtx?.post != null && (
            <p data-testid="erfolgs-subline" className="mt-0.5 text-xs text-muted-foreground">
              {subtitle}
            </p>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col">
        {!selectedFeedId ? (
          <WorkbenchEmptyState variant="no-feed" />
        ) : (
          <WorkbenchReviewer
            feedId={selectedFeedId}
            approvedAwaitingUpload={approvedAwaitingUpload}
            registerFlushPendingEdits={registerFlushPendingEdits}
            registerBulkRelease={registerBulkRelease}
            onUpload={openUpload}
            aktionen={
              eineZeile ? (
                <>
                  {bulkRelease && <WorkbenchReleaseMenu {...bulkRelease} />}
                  <WorkbenchUploadAction feedId={selectedFeedId} flushPendingEdits={flushPendingEdits} registerOpenUpload={registerOpenUpload} />
                </>
              ) : undefined
            }
          />
        )}
      </div>
    </div>
  );
};

export default Workbench;
